import { post, endpoints } from 'api/index';

const FALLBACK_ERROR =
  'Could not download the session images. Please try again.';

/*
 * The panorama is composited server-side. The browser cannot do it: session
 * photos are served from a bucket that sends no CORS headers, so drawing them
 * into a canvas taints it and toBlob() throws. The API hands back a JPEG, and
 * everything here is about turning that into a file the user actually gets.
 */

// Because the request asks for a blob, axios wraps the *error* body in a Blob
// too, so the JSON envelope has to be read back out of it before the message
// is usable. Anything unexpected falls back rather than showing the user a
// stringified Blob.
const readErrorMessage = async error => {
  const body = error?.data;
  if (!body) return FALLBACK_ERROR;

  try {
    const text = typeof body.text === 'function' ? await body.text() : body;
    return JSON.parse(text)?.message || FALLBACK_ERROR;
  } catch (parseError) {
    // A gateway timeout or a proxy error returns HTML, not our envelope.
    return FALLBACK_ERROR;
  }
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// The API picks the format from the strip's dimensions: JPEG cannot describe
// one wider than 65535px, so a long session comes back as PNG. Take the
// extension from what actually arrived rather than assuming, or the file gets
// saved under a name that misreports its contents.
const EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png'
};

// The server names the file "<category> - <session id>.<ext>", and that name is
// the one the user should get. It only arrives in Content-Disposition, so it has
// to be read off the response rather than rebuilt here -- the category is not
// something this module knows.
//
// Readable same-origin; cross-origin (a dev server against a remote API) needs
// the API to list the header in Access-Control-Expose-Headers, which it does.
// If it is missing either way, fall back to the session id so the download still
// works with a less descriptive name.
const filenameFromResponse = (response, sessionId, blobType) => {
  const disposition =
    response?.headers?.['content-disposition'] ||
    response?.headers?.['Content-Disposition'] ||
    '';

  // RFC 5987 form first: filename*=UTF-8''Frozen%20Food%20-%20....jpg
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch (e) {
      // Malformed percent-escapes; fall through to the plain form.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  if (plain) return plain[1].trim();

  return `${sessionId}.${EXTENSIONS[blobType] || 'jpg'}`;
};

// The response has no Content-Length when it comes back chunked, and axios
// reports total as 0 in that case. Passing it straight through lets the caller
// decide what to show rather than inventing a denominator here.
const toProgress = event => ({
  loaded: event.loaded || 0,
  total: event.total || 0
});

/**
 * Download every image in the session as one stacked panoramic image.
 * Resolves to null on success, or a message to show the user on failure.
 *
 * onProgress, if given, is called with {loaded, total} as the response
 * transfers. Nothing is emitted until the first byte arrives, which is the only
 * signal available that the server has stopped compositing and started
 * sending -- the request itself is silent until then.
 */
const downloadStackedImage = async ({
  photoIds,
  sessionId,
  gap = 0,
  onProgress
}) => {
  if (!photoIds?.length && !sessionId) return FALLBACK_ERROR;

  // photo_ids is what makes the download match the screen: the server stitches
  // exactly these, in exactly this order. session_id rides along only so the
  // filename and any server-side logging can name the session.
  const { response, error } = await post({
    uriPath: endpoints.storeExplorer.stackedImage,
    headers: ['auth'],
    payload: {
      ...(photoIds?.length ? { photo_ids: photoIds } : {}),
      session_id: sessionId,
      gap
    },
    responseType: 'blob',
    onDownloadProgress: onProgress
      ? event => onProgress(toProgress(event))
      : undefined
  });

  if (error) return readErrorMessage(error);

  // A 200 carrying JSON means the envelope came back on a path that did not
  // reject; treat it as a failure rather than saving an unopenable image.
  const blob = response?.data;
  if (!blob || blob.size === 0) return FALLBACK_ERROR;
  if (blob.type && blob.type.indexOf('image/') !== 0) {
    return readErrorMessage({ data: blob });
  }

  saveBlob(blob, filenameFromResponse(response, sessionId, blob.type));
  return null;
};

export default downloadStackedImage;
